import { cn } from "@/lib/utils";
import { Download, Loader2 } from "lucide-react";
import { PropsWithChildren, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import StyledDrawer from "../StyledDrawer";
import { Button, ButtonProps } from "../ui/button";
import { CustomDialogFooter } from "../ui/custom-dialog";
import { SheetClose } from "../ui/sheet";
import ExportButton from "./ExportButton";
import PrintButton from "./PrintButton";

const PrintDrawerTitle = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      <span className="font-semibold font-[Raleway] text-lg">
        {t("printTemplate")}
      </span>
      <span className="font-normal font-[Raleway] text-sm">
        {t("printSheetDescription")}
      </span>
    </div>
  );
};

const PrintDrawerFooter = ({
  submitButtonProps,
  formId,
  isLastTab,
  onNextTab,
}: Pick<PrintDrawerProps, "submitButtonProps" | "formId" | "isLastTab" | "onNextTab">) => {
  const { t } = useTranslation();

  const handleClick = (e: React.MouseEvent) => {
    if (!isLastTab && onNextTab) {
      e.preventDefault();
      onNextTab();
    }
    // If isLastTab, let the form submit naturally
  };

  return (
    <CustomDialogFooter className="flex flex-row flex-1 items-center gap-4">
      <SheetClose className="flex flex-1">
        <Button
          type="button"
          variant="outline"
          className="flex flex-1 bg-white self-end place-self-end"
        >
          {t("actionCancel")}
        </Button>
      </SheetClose>

      <Button
        {...submitButtonProps}
        type={isLastTab ? "submit" : "button"}
        className={cn("flex flex-1", submitButtonProps?.className)}
        form={isLastTab ? formId : undefined}
        disabled={submitButtonProps?.disabled || submitButtonProps?.isLoading}
        onClick={handleClick}
      >
        {submitButtonProps?.isLoading ? (
          <Loader2 className="animate-spin" />
        ) : isLastTab ? (
          t("overview")
        ) : (
          t("next")
        )}
      </Button>
    </CustomDialogFooter>
  );
};

type PrintDrawerProps = PropsWithChildren<{
  formId: string;
  submitButtonProps?: ButtonProps & { isLoading?: boolean };
  hideText?: boolean;
  iconSize?: number;
  iconColor?: string;
  isLastTab?: boolean;
  isOnPreviewTab?: boolean;
  onNextTab?: () => void;
  /** Called when the drawer opens or closes */
  onOpenChange?: (open: boolean) => void;
  /** Controlled open state (requis pour `keepContentMounted`). */
  open?: boolean;
  /** Garde le contenu monté à la fermeture → préserve les champs RHF (cache). */
  keepContentMounted?: boolean;
  /** Custom footer for preview tab */
  previewFooter?: ReactNode;
  /** Renders a full-width menu row trigger (for FormHeader Actions menu). */
  menuItemTrigger?: boolean;
  /** Renders the labelled "Exporter" trigger with tooltip (Essentiel sheet header). */
  labeledTrigger?: boolean;
}>;

const PrintDrawer = ({
  children,
  formId,
  submitButtonProps,
  hideText,
  iconSize,
  iconColor,
  isLastTab = true,
  isOnPreviewTab = false,
  onNextTab,
  onOpenChange,
  open,
  keepContentMounted = false,
  previewFooter,
  menuItemTrigger = false,
  labeledTrigger = false,
}: PrintDrawerProps) => {
  const { t } = useTranslation();

  const trigger = menuItemTrigger ? (
    <Button
      type="button"
      variant="ghost"
      className="h-auto w-full justify-start gap-2 rounded-sm px-2 py-1.5 text-sm font-normal shadow-none hover:bg-slate-200 focus:bg-slate-200 focus-visible:bg-slate-200"
    >
      <Download className="h-4 w-4 shrink-0 text-[#64748B]" aria-hidden />
      {t("singleArtworkPrintBtn")}
    </Button>
  ) : labeledTrigger ? (
    <ExportButton />
  ) : (
    <PrintButton hideText={hideText} iconSize={iconSize} iconColor={iconColor} />
  );

  return (
    <StyledDrawer
      title={<PrintDrawerTitle />}
      trigger={trigger}
      footer={
        isOnPreviewTab ? (
          previewFooter
        ) : (
          <PrintDrawerFooter
            submitButtonProps={submitButtonProps}
            formId={formId}
            isLastTab={isLastTab}
            onNextTab={onNextTab}
          />
        )
      }
      className="min-w-[900px] max-w-[90%]"
      sheetProps={{ open, onOpenChange }}
      keepContentMounted={keepContentMounted}
      hideSeparator
    >
      <div>{children}</div>
    </StyledDrawer>
  );
};

export default PrintDrawer;
